import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Header } from '../components/Header';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Switch } from '../components/ui/switch';
import { 
  AlertTriangle, 
  Heart, 
  Phone, 
  Camera, 
  FileCheck, 
  PenTool,
  ArrowLeft,
  ArrowRight,
  Check
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formSteps = [
  { id: 1, title: 'Allergies', icon: AlertTriangle, description: 'Food & environmental allergies' },
  { id: 2, title: 'Medical', icon: Heart, description: 'Medical conditions to be aware of' },
  { id: 3, title: 'Emergency', icon: Phone, description: 'Emergency contact information' },
  { id: 4, title: 'Consent', icon: Camera, description: 'Photo & video permissions' },
  { id: 5, title: 'Sign', icon: PenTool, description: 'Review and sign the form' }
];

const allergyOptions = [
  { id: 'peanuts', label: 'Peanuts' },
  { id: 'tree_nuts', label: 'Tree Nuts' },
  { id: 'dairy', label: 'Dairy' },
  { id: 'gluten', label: 'Gluten' },
  { id: 'eggs', label: 'Eggs' },
  { id: 'soy', label: 'Soy' },
  { id: 'shellfish', label: 'Shellfish' },
  { id: 'other', label: 'Other (specify below)' }
];

const medicalOptions = [
  { id: 'asthma', label: 'Asthma' },
  { id: 'epilepsy', label: 'Epilepsy' },
  { id: 'diabetes', label: 'Diabetes' },
  { id: 'adhd', label: 'ADHD' },
  { id: 'autism', label: 'Autism' },
  { id: 'other', label: 'Other (specify below)' }
];

export const AllergyForm = () => {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [booking, setBooking] = useState(null);

  // Form state
  const [allergies, setAllergies] = useState([]);
  const [allergyOther, setAllergyOther] = useState('');
  const [medicalConditions, setMedicalConditions] = useState([]);
  const [medicalOther, setMedicalOther] = useState('');
  const [emergencyName, setEmergencyName] = useState('');
  const [emergencyPhone, setEmergencyPhone] = useState('');
  const [emergencyRelationship, setEmergencyRelationship] = useState('');
  const [photoConsent, setPhotoConsent] = useState(false);
  const [liabilityAccepted, setLiabilityAccepted] = useState(false);
  const [signatureName, setSignatureName] = useState('');

  useEffect(() => {
    fetchBooking();
  }, [bookingId]);

  const fetchBooking = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/bookings/${bookingId}`);
      setBooking(response.data);
    } catch (error) {
      toast.error('Failed to load booking details');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const toggleAllergy = (id) => {
    setAllergies(prev => 
      prev.includes(id) ? prev.filter(a => a !== id) : [...prev, id]
    );
  };

  const toggleMedical = (id) => {
    setMedicalConditions(prev => 
      prev.includes(id) ? prev.filter(m => m !== id) : [...prev, id]
    );
  };

  const handleNext = () => {
    if (currentStep === 3 && (!emergencyName || !emergencyPhone || !emergencyRelationship)) {
      toast.error('Please fill in all emergency contact details');
      return;
    }
    if (currentStep < 5) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    if (!liabilityAccepted) {
      toast.error('Please accept the liability waiver to continue');
      return;
    }
    if (!signatureName) {
      toast.error('Please sign the form with your name');
      return;
    }

    try {
      const formData = {
        booking_id: bookingId,
        allergies,
        allergy_other: allergyOther || null,
        medical_conditions: medicalConditions,
        medical_other: medicalOther || null,
        emergency_contact_name: emergencyName,
        emergency_contact_phone: emergencyPhone,
        emergency_contact_relationship: emergencyRelationship,
        photo_video_consent: photoConsent,
        liability_waiver_accepted: liabilityAccepted,
        signature_name: signatureName,
        signature_date: new Date().toISOString().split('T')[0]
      };

      await axios.post(`${API_URL}/api/allergy-form`, formData);
      toast.success('Form submitted successfully!');
      navigate(`/booking/${bookingId}/payment`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit form');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FFFDF8]">
        <Header />
        <div className="flex items-center justify-center h-[60vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#FF3B6B]"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FFFDF8]">
      <Header />

      <div className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-semibold text-[#1E1E24] mb-2" style={{ fontFamily: 'Fredoka, sans-serif' }}>
            Allergy & Consent Form
          </h1>
          <p className="text-[#6B6B7A]">
            For {booking?.child_name}'s party on {new Date(booking?.date).toLocaleDateString('en-CA', { 
              weekday: 'long', month: 'long', day: 'numeric' 
            })}
          </p>
        </div>

        {/* Step Progress */}
        <div className="flex justify-between items-center mb-8 overflow-x-auto pb-4">
          {formSteps.map((step, index) => (
            <div key={step.id} className="flex items-center">
              <div className="flex flex-col items-center min-w-[80px]">
                <div 
                  className={`w-12 h-12 rounded-full flex items-center justify-center transition-all ${
                    currentStep >= step.id 
                      ? 'bg-[#FF3B6B] text-white' 
                      : 'bg-[#E8E5DF] text-[#6B6B7A]'
                  }`}
                  data-testid={`form-step-${step.id}`}
                >
                  {currentStep > step.id ? (
                    <Check className="w-6 h-6" />
                  ) : (
                    <step.icon className="w-5 h-5" />
                  )}
                </div>
                <span className={`text-xs mt-2 text-center ${
                  currentStep >= step.id ? 'text-[#1E1E24]' : 'text-[#6B6B7A]'
                }`}>
                  {step.title}
                </span>
              </div>
              {index < formSteps.length - 1 && (
                <div className={`w-12 h-1 mx-1 ${
                  currentStep > step.id ? 'bg-[#FF3B6B]' : 'bg-[#E8E5DF]'
                }`} />
              )}
            </div>
          ))}
        </div>

        {/* Form Content */}
        <motion.div
          key={currentStep}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.3 }}
          className="bg-white rounded-2xl border border-[#E8E5DF] p-6 sm:p-8"
        >
          {/* Step 1: Allergies */}
          {currentStep === 1 && (
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-full bg-[#FF3B6B]/10 flex items-center justify-center">
                  <AlertTriangle className="w-6 h-6 text-[#FF3B6B]" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-[#1E1E24]" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                    Section A - Allergies
                  </h2>
                  <p className="text-sm text-[#6B6B7A]">Select all allergies that apply</p>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                {allergyOptions.map((option) => (
                  <label
                    key={option.id}
                    className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                      allergies.includes(option.id)
                        ? 'border-[#FF3B6B] bg-[#FF3B6B]/5'
                        : 'border-[#E8E5DF] hover:border-[#FF3B6B]/50'
                    }`}
                    data-testid={`allergy-${option.id}`}
                  >
                    <Checkbox
                      checked={allergies.includes(option.id)}
                      onCheckedChange={() => toggleAllergy(option.id)}
                    />
                    <span className="text-sm text-[#1E1E24]">{option.label}</span>
                  </label>
                ))}
              </div>

              {allergies.includes('other') && (
                <div>
                  <Label className="text-[#1E1E24] font-semibold">Please specify other allergies</Label>
                  <Input
                    value={allergyOther}
                    onChange={(e) => setAllergyOther(e.target.value)}
                    placeholder="Describe other allergies..."
                    className="mt-2 rounded-xl border-[#E8E5DF]"
                    data-testid="allergy-other-input"
                  />
                </div>
              )}

              {allergies.length === 0 && (
                <div className="bg-[#06D6A0]/10 p-4 rounded-xl">
                  <p className="text-sm text-[#06D6A0]">No allergies selected - proceed to next section if none apply</p>
                </div>
              )}
            </div>
          )}

          {/* Step 2: Medical Conditions */}
          {currentStep === 2 && (
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-full bg-[#06D6A0]/10 flex items-center justify-center">
                  <Heart className="w-6 h-6 text-[#06D6A0]" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-[#1E1E24]" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                    Section B - Medical Conditions
                  </h2>
                  <p className="text-sm text-[#6B6B7A]">Select any medical conditions we should be aware of</p>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
                {medicalOptions.map((option) => (
                  <label
                    key={option.id}
                    className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                      medicalConditions.includes(option.id)
                        ? 'border-[#06D6A0] bg-[#06D6A0]/5'
                        : 'border-[#E8E5DF] hover:border-[#06D6A0]/50'
                    }`}
                    data-testid={`medical-${option.id}`}
                  >
                    <Checkbox
                      checked={medicalConditions.includes(option.id)}
                      onCheckedChange={() => toggleMedical(option.id)}
                    />
                    <span className="text-sm text-[#1E1E24]">{option.label}</span>
                  </label>
                ))}
              </div>

              {medicalConditions.includes('other') && (
                <div>
                  <Label className="text-[#1E1E24] font-semibold">Please specify other conditions</Label>
                  <Input
                    value={medicalOther}
                    onChange={(e) => setMedicalOther(e.target.value)}
                    placeholder="Describe other medical conditions..."
                    className="mt-2 rounded-xl border-[#E8E5DF]"
                    data-testid="medical-other-input"
                  />
                </div>
              )}

              {medicalConditions.length === 0 && (
                <div className="bg-[#06D6A0]/10 p-4 rounded-xl">
                  <p className="text-sm text-[#06D6A0]">No conditions selected - proceed to next section if none apply</p>
                </div>
              )}
            </div>
          )}

          {/* Step 3: Emergency Contact */}
          {currentStep === 3 && (
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-full bg-[#FFD166]/20 flex items-center justify-center">
                  <Phone className="w-6 h-6 text-[#B8860B]" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-[#1E1E24]" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                    Section C - Emergency Contact
                  </h2>
                  <p className="text-sm text-[#6B6B7A]">Who should we contact in case of emergency?</p>
                </div>
              </div>

              <div className="space-y-6">
                <div>
                  <Label htmlFor="emergencyName" className="text-[#1E1E24] font-semibold">Contact Name *</Label>
                  <Input
                    id="emergencyName"
                    value={emergencyName}
                    onChange={(e) => setEmergencyName(e.target.value)}
                    placeholder="Full name"
                    className="mt-2 rounded-xl border-[#E8E5DF]"
                    data-testid="emergency-name"
                  />
                </div>
                <div>
                  <Label htmlFor="emergencyPhone" className="text-[#1E1E24] font-semibold">Phone Number *</Label>
                  <Input
                    id="emergencyPhone"
                    type="tel"
                    value={emergencyPhone}
                    onChange={(e) => setEmergencyPhone(e.target.value)}
                    placeholder="(123) 456-7890"
                    className="mt-2 rounded-xl border-[#E8E5DF]"
                    data-testid="emergency-phone"
                  />
                </div>
                <div>
                  <Label htmlFor="emergencyRelationship" className="text-[#1E1E24] font-semibold">Relationship *</Label>
                  <Input
                    id="emergencyRelationship"
                    value={emergencyRelationship}
                    onChange={(e) => setEmergencyRelationship(e.target.value)}
                    placeholder="e.g., Parent, Grandparent, Guardian"
                    className="mt-2 rounded-xl border-[#E8E5DF]"
                    data-testid="emergency-relationship"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Photo/Video Consent */}
          {currentStep === 4 && (
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-full bg-[#FF3B6B]/10 flex items-center justify-center">
                  <Camera className="w-6 h-6 text-[#FF3B6B]" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-[#1E1E24]" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                    Section D - Photo/Video Consent
                  </h2>
                  <p className="text-sm text-[#6B6B7A]">Permission to capture party memories</p>
                </div>
              </div>

              <div className="bg-[#FFFDF8] p-6 rounded-xl border border-[#E8E5DF] mb-6">
                <p className="text-[#6B6B7A] mb-4">
                  We may take photos and videos during the party for promotional purposes, 
                  including social media, website, and marketing materials. Your child's image 
                  will be used respectfully and professionally.
                </p>
                <div className="flex items-center justify-between p-4 bg-white rounded-xl border border-[#E8E5DF]">
                  <div>
                    <p className="font-semibold text-[#1E1E24]">Allow Photo/Video</p>
                    <p className="text-sm text-[#6B6B7A]">I consent to photos and videos of my child</p>
                  </div>
                  <Switch
                    checked={photoConsent}
                    onCheckedChange={setPhotoConsent}
                    data-testid="photo-consent-switch"
                  />
                </div>
              </div>

              <div className={`p-4 rounded-xl ${photoConsent ? 'bg-[#06D6A0]/10' : 'bg-[#FF3B6B]/10'}`}>
                <p className={`text-sm ${photoConsent ? 'text-[#06D6A0]' : 'text-[#FF3B6B]'}`}>
                  {photoConsent 
                    ? 'Photo/video consent granted. We will capture party memories!'
                    : 'Photo/video consent not granted. We will not take photos/videos of your child.'
                  }
                </p>
              </div>
            </div>
          )}

          {/* Step 5: Liability Waiver & Signature */}
          {currentStep === 5 && (
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-full bg-[#06D6A0]/10 flex items-center justify-center">
                  <FileCheck className="w-6 h-6 text-[#06D6A0]" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-[#1E1E24]" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                    Section E - Liability Waiver
                  </h2>
                  <p className="text-sm text-[#6B6B7A]">Please review and sign</p>
                </div>
              </div>

              <div className="bg-[#FFFDF8] p-6 rounded-xl border border-[#E8E5DF] mb-6 max-h-48 overflow-y-auto">
                <p className="text-sm text-[#6B6B7A] leading-relaxed">
                  By signing this form, I acknowledge that I have read and understood the terms and conditions 
                  of Creative Birthdays's party services. I understand that participation involves certain 
                  inherent risks, including but not limited to allergic reactions, minor injuries, and exposure 
                  to other children. I hereby release and discharge Creative Birthdays, its employees, and 
                  affiliates from any and all liability arising from my child's participation in the party 
                  activities.
                  <br /><br />
                  I confirm that all medical information provided is accurate and complete to the best of my 
                  knowledge. I authorize Creative Birthdays staff to seek emergency medical treatment for 
                  my child if deemed necessary.
                  <br /><br />
                  I understand that a 50% deposit is required to confirm the booking, with the balance due 
                  7 days before the party date. Cancellations made less than 48 hours before the party 
                  forfeit the deposit.
                </p>
              </div>

              <label 
                className={`flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all mb-6 ${
                  liabilityAccepted 
                    ? 'border-[#06D6A0] bg-[#06D6A0]/5' 
                    : 'border-[#E8E5DF]'
                }`}
                data-testid="liability-checkbox"
              >
                <Checkbox
                  checked={liabilityAccepted}
                  onCheckedChange={setLiabilityAccepted}
                  className="mt-1"
                />
                <span className="text-sm text-[#1E1E24]">
                  I have read, understood, and agree to the above liability waiver and terms. *
                </span>
              </label>

              <div>
                <Label className="text-[#1E1E24] font-semibold">Digital Signature *</Label>
                <p className="text-sm text-[#6B6B7A] mb-2">Type your full legal name to sign this form</p>
                <Input
                  value={signatureName}
                  onChange={(e) => setSignatureName(e.target.value)}
                  placeholder="Type your full name"
                  className="mt-2 rounded-xl border-[#E8E5DF] text-lg font-medium"
                  style={{ fontFamily: 'cursive' }}
                  data-testid="signature-input"
                />
                <p className="text-xs text-[#6B6B7A] mt-2">
                  Date: {new Date().toLocaleDateString('en-CA', { year: 'numeric', month: 'long', day: 'numeric' })}
                </p>
              </div>
            </div>
          )}

          {/* Navigation */}
          <div className="flex justify-between mt-8 pt-6 border-t border-[#E8E5DF]">
            <Button
              variant="outline"
              onClick={handleBack}
              disabled={currentStep === 1}
              className="rounded-full border-[#E8E5DF]"
              data-testid="form-btn-back"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            {currentStep < 5 ? (
              <Button
                onClick={handleNext}
                className="bg-[#FF3B6B] hover:bg-[#E62E58] text-white rounded-full btn-hover"
                data-testid="form-btn-next"
              >
                Next
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button
                onClick={handleSubmit}
                disabled={!liabilityAccepted || !signatureName}
                className="bg-[#06D6A0] hover:bg-[#05B88A] text-white rounded-full btn-hover"
                data-testid="form-btn-submit"
              >
                Submit & Continue to Payment
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default AllergyForm;
